var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { FundedOption } from '@interfaces/proposal.interface';
import AccountUtils from '@popup/steem/utils/account.utils';
import { SteemTxUtils } from '@popup/steem/utils/steem-tx.utils';
import moment from 'moment';
import Config from 'src/config';
import FormatUtils from 'src/utils/format.utils';
import { Asset as CommonAsset } from '@steempro/steem-keychain-commons';
const hasVotedForProposal = (username, proposalId) => __awaiter(void 0, void 0, void 0, function* () {
    const listProposalVotes = yield SteemTxUtils.getData('condenser_api.list_proposal_votes', [
        [
            proposalId !== undefined ? proposalId : Config.KEYCHAIN_PROPOSAL,
            username,
        ],
        1,
        'by_proposal_voter',
        'ascending',
        'all',
    ]);
    return listProposalVotes[0].voter === username;
});
/* istanbul ignore next */
const voteForKeychainProposal = (username, activeKey, options) => __awaiter(void 0, void 0, void 0, function* () {
    return yield SteemTxUtils.sendOperation([
        ProposalUtils.getUpdateProposalVoteOperation([Config.KEYCHAIN_PROPOSAL], true, username),
    ], activeKey, false, options);
});
/* istanbul ignore next */
const voteForProposal = (proposalId, username, activeKey, options) => __awaiter(void 0, void 0, void 0, function* () {
    return yield SteemTxUtils.sendOperation([
        ProposalUtils.getUpdateProposalVoteOperation([proposalId], true, username),
    ], activeKey, false, options);
});
/* istanbul ignore next */
const unvoteProposal = (proposalId, username, activeKey, options) => __awaiter(void 0, void 0, void 0, function* () {
    return yield SteemTxUtils.sendOperation([
        ProposalUtils.getUpdateProposalVoteOperation([proposalId], false, username),
    ], activeKey, false, options);
});
/* istanbul ignore next */
const updateProposalVotes = (proposalIds, username, approve, activeKey, options) => __awaiter(void 0, void 0, void 0, function* () {
    return yield SteemTxUtils.sendOperation([
        ProposalUtils.getUpdateProposalVoteOperation(proposalIds, approve, username),
    ], activeKey, false, options);
});
/* istanbul ignore next */
const getUpdateProposalVoteTransaction = (proposalIds, username, approve) => {
    return SteemTxUtils.createTransaction([
        ProposalUtils.getUpdateProposalVoteOperation(proposalIds, approve, username),
    ]);
};
/* istanbul ignore next */
const getUpdateProposalVoteOperation = (proposalId, approve, username) => {
    return [
        'update_proposal_votes',
        {
            voter: username,
            proposal_ids: proposalId,
            approve: approve,
            extensions: [],
        },
    ];
};
const getFundedOption = (dailyPay, dailyBudget) => {
    let fundedOption = FundedOption.NOT_FUNDED;
    if (dailyBudget > 0) {
        if (dailyBudget - dailyPay >= 0) {
            fundedOption = FundedOption.TOTALLY_FUNDED;
        }
        else {
            fundedOption = FundedOption.PARTIALLY_FUNDED;
        }
    }
    return fundedOption;
};
const getProposalList = (accountName, globals) => __awaiter(void 0, void 0, void 0, function* () {
    const listProposals = yield SteemTxUtils.getData('condenser_api.list_proposals', [[-1], 1000, 'by_total_votes', 'descending', 'votable']);
    const listProposalVotesRequestResult = yield SteemTxUtils.getData('condenser_api.list_proposal_votes', [[accountName], 1000, 'by_voter_proposal', 'descending', 'votable']);
    const listProposalVotes = listProposalVotesRequestResult
        .filter((item) => item.voter === accountName)
        .map((item) => item.proposal);
    let dailyBudget = yield ProposalUtils.getProposalDailyBudget();
    const proposals = [];
    for (const p of listProposals) {
        const dailyPay = CommonAsset.fromString(p.daily_pay);
        const fundedOption = ProposalUtils.getFundedOption(dailyPay.amount, dailyBudget);
        proposals.push({
            id: p.id,
            creator: p.creator,
            proposalId: p.proposal_id,
            subject: p.subject,
            receiver: p.receiver,
            dailyPay: dailyPay,
            link: `https://steempro.com/proposals/${p.proposal_id}`,
            startDate: moment(p.start_date),
            endDate: moment(p.end_date),
            totalVotes: `${FormatUtils.nFormatter(FormatUtils.toSP((parseFloat(p.total_votes) / 1000000).toString(), globals), 2)} SP`,
            voted: listProposalVotes.find((votedProposal) => votedProposal.proposal_id === p.proposal_id) !== undefined,
            funded: fundedOption,
        });
        dailyBudget = dailyBudget - dailyPay.amount;
    }
    return proposals;
});
const isRequestingProposalVotes = (globals) => __awaiter(void 0, void 0, void 0, function* () {
    let dailyBudget = +(yield ProposalUtils.getProposalDailyBudget());
    const proposals = (yield SteemTxUtils.getData('condenser_api.list_proposals', [
        [-1, 0],
        100,
        'by_total_votes',
        'descending',
        'all',
    ])).map((proposal) => {
        const dailyPay = CommonAsset.fromString(proposal.daily_pay);
        let fundedOption = FundedOption.NOT_FUNDED;
        if (dailyBudget > 0) {
            dailyBudget -= dailyPay.amount;
            if (dailyBudget >= 0) {
                fundedOption = FundedOption.TOTALLY_FUNDED;
            }
            else {
                fundedOption = FundedOption.PARTIALLY_FUNDED;
            }
        }
        proposal.fundedOption = fundedOption;
        proposal.totalVotes = FormatUtils.toSP((parseFloat(proposal.total_votes) / 1000000).toString(), globals);
        return proposal;
    });
    const keychainProposal = proposals.find((proposal) => proposal.id === Config.KEYCHAIN_PROPOSAL);
    const returnProposal = proposals.find((proposal) => proposal.fundedOption == FundedOption.PARTIALLY_FUNDED);
    const voteDifference = keychainProposal.totalVotes - returnProposal.totalVotes;
    return voteDifference < Config.PROPOSAL_MIN_VOTE_DIFFERENCE_HIDE_POPUP;
});
/* istanbul ignore next */
const getProposalDailyBudget = () => __awaiter(void 0, void 0, void 0, function* () {
    return (parseFloat((yield AccountUtils.getExtendedAccount('steem.dao')).sbd_balance
        .toString()
        .split(' ')[0]) / 100);
});
/* istanbul ignore next */
const createProposal = (username, receiver, startDate, endDate, dailyPay, subject, permlink, extensions, key, options) => {
    return SteemTxUtils.sendOperation([
        ProposalUtils.getCreateProposalOperation(username, receiver, startDate, endDate, dailyPay, subject, permlink, extensions),
    ], key, false, options);
};
/* istanbul ignore next */
const getCreateProposalOperation = (username, receiver, startDate, endDate, dailyPay, subject, permlink, extensions) => {
    return [
        'create_proposal',
        {
            creator: username,
            receiver: receiver,
            start_date: startDate,
            end_date: endDate,
            daily_pay: dailyPay,
            subject: subject,
            permlink: permlink,
            extensions: typeof extensions === 'string' ? JSON.parse(extensions) : extensions,
        },
    ];
};
/* istanbul ignore next */
const getCreateProposalTransaction = (username, receiver, startDate, endDate, dailyPay, subject, permlink, extensions) => {
    return SteemTxUtils.createTransaction([
        ProposalUtils.getCreateProposalOperation(username, receiver, startDate, endDate, dailyPay, subject, permlink, extensions),
    ]);
};
/* istanbul ignore next */
const removeProposal = (owner, ids, extensions, key, options) => {
    return SteemTxUtils.sendOperation([ProposalUtils.getRemoveProposalOperation(owner, ids, extensions)], key, false, options);
};
/* istanbul ignore next */
const getRemoveProposalOperation = (owner, ids, extensions) => {
    return [
        'remove_proposal',
        {
            proposal_owner: owner,
            proposal_ids: ids,
            extensions: typeof extensions === 'string' ? JSON.parse(extensions) : extensions,
        },
    ];
};
/* istanbul ignore next */
const getRemoveProposalTransaction = (owner, ids, extensions) => {
    return SteemTxUtils.createTransaction([
        ProposalUtils.getRemoveProposalOperation(owner, ids, extensions),
    ]);
};
const ProposalUtils = {
    hasVotedForProposal,
    voteForProposal,
    voteForKeychainProposal,
    getProposalList,
    unvoteProposal,
    isRequestingProposalVotes,
    getUpdateProposalVoteOperation,
    getUpdateProposalVoteTransaction,
    getProposalDailyBudget,
    getFundedOption,
    createProposal,
    updateProposalVotes,
    removeProposal,
    getCreateProposalOperation,
    getCreateProposalTransaction,
    getRemoveProposalOperation,
    getRemoveProposalTransaction,
};
export default ProposalUtils;
